import _ from 'lodash/fp'
import { decamelizeKeys } from 'humps'
import gql from 'graphql-tag'
import { put, call, takeLatest, all } from 'redux-saga/effects'

import { types, actions } from '../reducers/sponsorSetups'
import { getCurrentClubId } from './network'
import { apolloClient } from '@fe/business/src/apollo'

//  ███████╗███████╗███████╗███████╗ ██████╗████████╗███████╗
//  ██╔════╝██╔════╝██╔════╝██╔════╝██╔════╝╚══██╔══╝██╔════╝
//  █████╗  █████╗  █████╗  █████╗  ██║        ██║   ███████╗
//  ██╔══╝  ██╔══╝  ██╔══╝  ██╔══╝  ██║        ██║   ╚════██║
//  ███████╗██║     ██║     ███████╗╚██████╗   ██║   ███████║
//  ╚══════╝╚═╝     ╚═╝     ╚══════╝ ╚═════╝   ╚═╝   ╚══════╝

const CLUB_SPONSOR_SETUPS_QUERY = gql`
  query ClubSponsorSetups($clubId: ID!) {
    clubSponsorSetups(clubId: $clubId) {
      id
      clubId
      courseId
      name
      sponsorPositions {
        sponsorId
        sponsorSetupId
        holeId
        position
      }
    }
  }
`

const CREATE_SPONSOR_SETUP = gql`
  mutation CreateSponsorSetup($input: CreateSponsorSetupInput!) {
    createSponsorSetup(input: $input) {
      id
    }
  }
`

const SAVE_SPONSOR_SETUP = gql`
  mutation SaveSponsorSetup($input: SaveSponsorSetupInput!) {
    saveSponsorSetup(input: $input) {
      id
      name
    }
  }
`

const DELETE_SPONSOR_SETUP = gql`
  mutation DeleteSponsorSetup($input: DeleteSponsorSetupInput!) {
    deleteSponsorSetup(input: $input)
  }
`

// GraphQL ID scalars always serialize as strings, but sponsorSetupsByCourseId compares
// course_id against a parsed integer, matching the legacy REST API's native JSON numbers.
// hole_id/sponsor_id/sponsor_setup_id are left as-is - they're compared against other
// GraphQL-sourced string IDs (e.g. course.holes[].id) elsewhere, so must stay strings too.
const toLegacyShape = (sponsorSetup) => ({
  ...sponsorSetup,
  course_id: Number(sponsorSetup.course_id)
})

export function * loadSponsorSetupsEffect () {
  try {
    const clubId = yield call(getCurrentClubId)
    const response = yield call([apolloClient, apolloClient.query], {
      query: CLUB_SPONSOR_SETUPS_QUERY,
      variables: { clubId },
      fetchPolicy: 'network-only'
    })
    const sponsorSetups = _.getOr([], 'data.clubSponsorSetups', response)
    // Reducer/selectors consume snake_case keys (course_id, sponsor_positions, ...) matching the legacy REST shape
    return decamelizeKeys(sponsorSetups).map(toLegacyShape)
  } catch (error) {
    return { error }
  }
}

export function * saveSponsorSetupEffect (sponsorSetup) {
  const { id, name, sponsor_positions: sponsorPositions } = sponsorSetup
  try {
    const clubId = yield call(getCurrentClubId)
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: SAVE_SPONSOR_SETUP,
      variables: {
        input: {
          clubId,
          sponsorSetupId: id,
          name,
          sponsorPositions: (sponsorPositions || []).map((sp) => ({
            sponsorId: sp.sponsor_id,
            holeId: sp.hole_id,
            position: sp.position
          }))
        }
      }
    })
    return _.get('data.saveSponsorSetup', response)
  } catch (error) {
    return { error }
  }
}

export function * createSponsorSetupEffect (sponsorSetup) {
  const { club_id: clubId, course_id: courseId, name } = sponsorSetup
  try {
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: CREATE_SPONSOR_SETUP,
      variables: { input: { clubId, courseId, name } }
    })
    return _.get('data.createSponsorSetup', response)
  } catch (error) {
    return { error }
  }
}

export function * deleteSponsorSetupEffect (sponsorSetupId) {
  try {
    const clubId = yield call(getCurrentClubId)
    yield call([apolloClient, apolloClient.mutate], {
      mutation: DELETE_SPONSOR_SETUP,
      variables: { input: { clubId, sponsorSetupId } }
    })
    return {}
  } catch (error) {
    return { error }
  }
}

//  ███████╗ █████╗  ██████╗  █████╗ ███████╗
//  ██╔════╝██╔══██╗██╔════╝ ██╔══██╗██╔════╝
//  ███████╗███████║██║  ███╗███████║███████╗
//  ╚════██║██╔══██║██║   ██║██╔══██║╚════██║
//  ███████║██║  ██║╚██████╔╝██║  ██║███████║
//  ╚══════╝╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═╝╚══════╝

export function * loadSponsorSetupsFlow () {
  const sponsors = yield call(loadSponsorSetupsEffect)

  if (sponsors.error) {
    yield put(actions.loadError(sponsors))
  } else {
    yield put(actions.loadSuccess(sponsors))
  }

  yield put(actions.loadRequestDone())
}

export function * saveSponsorSetupFlow ({ payload }) {
  const { sponsorSetup } = payload
  const sponsorSetupId = sponsorSetup.id
  if (!sponsorSetupId) {
    const sponsorSetupWithoutPositions = _.flow(
      _.set('sponsor_positions', [])
    )(sponsorSetup)
    const res = yield call(createSponsorSetupEffect, sponsorSetupWithoutPositions)
    if (res.error) {
    } else {
      const sponsorSetupFinal = {
        ...sponsorSetup,
        id: res.id,
        sponsor_positions: _.map(_.set('sponsor_setup_id', res.id), sponsorSetup.sponsor_positions)
      }
      yield call(saveSponsorSetupEffect, sponsorSetupFinal)

      yield put(actions.saveDone({ sponsorSetupId }))
      return yield put(actions.loadRequest())
    }
  } else {
    yield call(saveSponsorSetupEffect, sponsorSetup)
    yield put(actions.saveDone({ sponsorSetupId }))
    return yield put(actions.loadRequest())
  }
}

export function * deleteSponsorSetupFlow ({ payload }) {
  const { sponsorSetupId } = payload
  yield call(deleteSponsorSetupEffect, sponsorSetupId)

  yield put(actions.deleteDone(sponsorSetupId))
  yield put(actions.loadRequest())
}

export function * initiateUpdateFlow (objectWithPayload) {
  const { payload } = objectWithPayload
  yield call(loadSponsorSetupsFlow, objectWithPayload)

  yield put(actions.initiateUpdateResult(payload))
}

export default function * () {
  return yield all([
    takeLatest(types.LOAD_REQUEST, loadSponsorSetupsFlow),
    takeLatest(types.SAVE, saveSponsorSetupFlow),
    takeLatest(types.DELETE, deleteSponsorSetupFlow),
    takeLatest(types.INITIATE_UPDATE, initiateUpdateFlow)
  ])
}
